import { z } from "zod";

const ProgressStateSchema = z.enum(["notShown", "shown", "learned", "mastered"]);
export type PracticalProgressState = z.infer<typeof ProgressStateSchema>;

interface ProgressContent {
  id: string;
  title: string;
  description?: string;
  link?: string;
  linkLabel?: string;
}

export type PracticalProgressSkill = ProgressContent & {
  type: "skill";
  order: number;
  state: PracticalProgressState;
};

export type PracticalProgressCategory = ProgressContent & {
  type: "category";
  order: number;
  children: PracticalProgressNode[];
  percentage: number;
  emptyMessage?: string;
};

export type PracticalProgressNode = PracticalProgressSkill | PracticalProgressCategory;
export interface PracticalProgress {
  nodes: PracticalProgressNode[];
  percentage: number;
}

type TemplateNode = {
  title: string;
  order: number;
  subline?: string | null;
  link?: string | null;
} & ({ type: "checkbox" } | { type: "node"; children?: Record<string, TemplateNode> | null });

const InformationLinkSchema = z.union([
  z.literal(""),
  z
    .string()
    .url()
    .refine((value) => ["http:", "https:"].includes(new URL(value).protocol)),
]);
const TemplateContentSchema = z.object({
  title: z.string(),
  order: z.number().finite(),
  subline: z.string().nullish(),
  link: InformationLinkSchema.nullish(),
});
const TemplateNodeSchema: z.ZodType<TemplateNode> = z.lazy(() =>
  z.discriminatedUnion("type", [
    TemplateContentSchema.extend({ type: z.literal("checkbox") }),
    TemplateContentSchema.extend({ type: z.literal("node"), children: z.record(TemplateNodeSchema).nullish() }),
  ]),
);
const ProgressEntrySchema = z.object({
  state: ProgressStateSchema.nullish(),
  checkbox: z.boolean().nullish(),
  type: z.string().nullish(),
});
const pointsByState = { notShown: 0, shown: 0.33, learned: 0.66, mastered: 1 };

export function buildPracticalProgress(
  templateData: unknown,
  progressData: unknown,
  additionalNodes: { adas: ProgressContent[]; pois: ProgressContent[]; hasExamLocation: boolean },
): PracticalProgress {
  const template = z.record(TemplateNodeSchema).parse(templateData);
  const progress = z.record(ProgressEntrySchema).parse(progressData ?? {});
  const templateIds = new Set<string>();

  function getState(id: string): PracticalProgressState {
    const entry = progress[id];
    return entry?.state ?? (entry?.checkbox ? "mastered" : "notShown");
  }

  function buildNodes(nodes: Record<string, TemplateNode>): PracticalProgressNode[] {
    return Object.entries(nodes)
      .map(([id, node]): PracticalProgressNode => {
        templateIds.add(id);
        const content = {
          id,
          title: node.title,
          description: node.subline ?? "",
          order: node.order,
          link: node.link || undefined,
        };
        if (node.type === "checkbox") {
          return { ...content, type: "skill", state: getState(id) };
        }
        const children = buildNodes(node.children ?? {});
        return { ...content, type: "category", children, percentage: calculatePracticalProgress(children) };
      })
      .sort((a, b) => a.order - b.order);
  }

  const nodes = buildNodes(template);
  for (const category of [
    {
      id: "adas",
      title: "Assistenzsysteme",
      type: "fromAdas",
      entries: additionalNodes.adas,
      emptyMessage: "Für die Fahrzeuge der abgeschlossenen Fahrstunden sind keine Assistenzsysteme verfügbar.",
    },
    {
      id: "poi",
      title: "Besondere Orte",
      type: "fromPoi",
      entries: additionalNodes.pois,
      emptyMessage: additionalNodes.hasExamLocation
        ? "Für den Prüfungsort sind keine besonderen Orte hinterlegt."
        : "Es ist noch kein Prüfungsort hinterlegt.",
    },
  ]) {
    const entries = [...new Map(category.entries.map((entry) => [entry.id, entry])).values()];
    const children: PracticalProgressSkill[] = entries.map((entry, order) => ({
      ...entry,
      type: "skill",
      order,
      state: !templateIds.has(entry.id) && progress[entry.id]?.type === category.type ? getState(entry.id) : "notShown",
    }));
    nodes.push({
      id: category.id,
      title: category.title,
      type: "category",
      order: 9999,
      children,
      percentage: calculatePracticalProgress(children),
      emptyMessage: category.emptyMessage,
    });
  }
  nodes.sort((a, b) => a.order - b.order);
  return { nodes, percentage: calculatePracticalProgress(nodes) };
}

export function calculatePracticalProgress(nodes: PracticalProgressNode[]): number {
  let points = 0;
  let count = 0;
  function visit(children: PracticalProgressNode[]) {
    for (const node of children) {
      if (node.type === "category") {
        visit(node.children);
      } else {
        points += pointsByState[node.state];
        count++;
      }
    }
  }
  visit(nodes);
  // Match the mobile app: count skills, not categories, and keep the 0.33/0.66 weights.
  return count === 0 ? 0 : (points / count) * 100;
}

export function formatPracticalProgress(percentage: number): string {
  return `${percentage.toLocaleString("de-DE", { maximumFractionDigits: 1 })}%`;
}

export function searchPracticalProgress(
  nodes: PracticalProgressNode[],
  search: string,
  ancestors: PracticalProgressCategory[] = [],
): { node: PracticalProgressNode; ancestors: PracticalProgressCategory[] }[] {
  const term = search.trim().toLocaleLowerCase("de-DE");
  return nodes.flatMap((node) => {
    const matches = `${node.title}\n${node.description ?? ""}`.toLocaleLowerCase("de-DE").includes(term);
    return [
      ...(matches ? [{ node, ancestors }] : []),
      ...(node.type === "category" ? searchPracticalProgress(node.children, term, [...ancestors, node]) : []),
    ];
  });
}
